import apiClient from './api';

export const authService = {
  async login(email, password) {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  async register(userData) {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  async getCurrentUser() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  // Password Reset
  async requestPasswordReset(email) {
    const response = await apiClient.post('/auth/password-reset/request', { email });
    return response.data;
  },

  async confirmPasswordReset(token, newPassword) {
    const response = await apiClient.post('/auth/password-reset/confirm', {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  async changePassword(currentPassword, newPassword) {
    const response = await apiClient.post('/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  // SSO
  async getSSOStatus() {
    const response = await apiClient.get('/auth/entra/status');
    return response.data;
  },

  getSSOLoginUrl() {
    return `${process.env.REACT_APP_BACKEND_URL}/api/auth/entra/login`;
  },

  // User Management (Admin)
  async getUsers() {
    const response = await apiClient.get('/auth/users');
    return response.data;
  },

  async createUser(userData) {
    const response = await apiClient.post('/auth/users', userData);
    return response.data;
  },

  async updateUser(userId, updateData) {
    const response = await apiClient.put(`/auth/users/${userId}`, updateData);
    return response.data;
  },

  async deleteUser(userId) {
    const response = await apiClient.delete(`/auth/users/${userId}`);
    return response.data;
  },

  async resetUserPassword(userId) {
    const response = await apiClient.post(`/auth/users/${userId}/reset-password`);
    return response.data;
  },

  storeToken(token) {
    localStorage.setItem('access_token', token);
  },

  storeUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  },

  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  getToken() {
    return localStorage.getItem('access_token');
  },

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  },

  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }
};

// SSO Configuration Service (Admin)
export const ssoService = {
  async getConfig() {
    const response = await apiClient.get('/auth/entra/config');
    return response.data;
  },

  async updateConfig(config) {
    const response = await apiClient.post('/auth/entra/config', config);
    return response.data;
  }
};

export const invoiceService = {
  async getInvoices(params = {}) {
    const response = await apiClient.get('/books/invoices', { params });
    return response.data;
  },

  async getInvoiceDetail(invoiceId) {
    const response = await apiClient.get(`/books/invoices/${invoiceId}`);
    return response.data;
  },

  async getCreditNotes(params = {}) {
    const response = await apiClient.get('/books/creditnotes', { params });
    return response.data;
  },

  async downloadPdf(invoiceId) {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${apiClient.defaults.baseURL}/books/invoices/${invoiceId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res;
  },

  async downloadEInvoice(invoiceId) {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${apiClient.defaults.baseURL}/books/invoices/${invoiceId}/einvoice`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res;
  },

  async getDashboardStats() {
    const response = await apiClient.get('/books/dashboard-stats');
    return response.data;
  }
};

export const ticketService = {
  async getTickets(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.limit) params.append('limit', filters.limit);
    
    const queryString = params.toString();
    const url = queryString ? `/desk/tickets?${queryString}` : '/desk/tickets';
    const response = await apiClient.get(url);
    return response.data;
  },
  
  async getTicketDetail(ticketId) {
    const response = await apiClient.get(`/desk/tickets/${ticketId}`);
    return response.data;
  },
  
  async getTicketThreads(ticketId) {
    const response = await apiClient.get(`/desk/tickets/${ticketId}/threads`);
    return response.data;
  },
  
  async addTicketComment(ticketId, content, isPublic = true) {
    const response = await apiClient.post(`/desk/tickets/${ticketId}/comments`, {
      content,
      is_public: isPublic
    });
    return response.data;
  },
  
  async getDepartments() {
    const response = await apiClient.get('/desk/departments');
    return response.data;
  },
  
  async createTicket(ticketData) {
    const response = await apiClient.post('/desk/tickets', ticketData);
    return response.data;
  }
};

export const crmService = {
  async getProfile() {
    const response = await apiClient.get('/crm/profile');
    return response.data;
  },

  async getInvoices(limit = 50) {
    const response = await apiClient.get('/crm/invoices', { params: { limit } });
    return response.data;
  },

  async getInvoiceDetail(invoiceId) {
    const response = await apiClient.get(`/crm/invoices/${invoiceId}`);
    return response.data;
  }
};

export const adminService = {
  async getCredentialStatus(service) {
    const response = await apiClient.get(`/admin/credentials/${service}`);
    return response.data;
  },

  async configureCredentials(credentials) {
    const response = await apiClient.post('/admin/credentials', credentials);
    return response.data;
  },

  async getSystemHealth() {
    const response = await apiClient.get('/admin/system-health');
    return response.data;
  },

  async getWebhookConfig() {
    const response = await apiClient.get('/admin/webhook-config');
    return response.data;
  },

  async configureWebhook(config) {
    const response = await apiClient.post('/admin/webhook-config', config);
    return response.data;
  },

  async testConnection(service) {
    const response = await apiClient.post(`/admin/test-connection/${service}`);
    return response.data;
  },

  async getOAuthStatus(service) {
    const response = await apiClient.get(`/oauth/${service}/status`);
    return response.data;
  },

  async initiateOAuth(service, region = 'eu') {
    const response = await apiClient.get(`/oauth/${service}/authorize?region=${region}`);
    return response.data;
  },

  async refreshOAuthToken(service) {
    const response = await apiClient.post(`/oauth/${service}/refresh`);
    return response.data;
  },

  // Email Config
  async getEmailConfig() {
    const response = await apiClient.get('/admin/email-config');
    return response.data;
  },

  async saveEmailConfig(config) {
    const response = await apiClient.post('/admin/email-config', config);
    return response.data;
  },

  async sendTestEmail(recipient) {
    const response = await apiClient.post('/admin/email-test', { recipient });
    return response.data;
  }
};

// RBAC Service - Roles & Permissions
export const rbacService = {
  // Roles
  async getRoles(includeCustom = true, tenantId = null) {
    const params = new URLSearchParams();
    params.append('include_custom', includeCustom);
    if (tenantId) params.append('tenant_id', tenantId);
    const response = await apiClient.get(`/rbac/roles?${params.toString()}`);
    return response.data;
  },

  async getRoleDetail(roleId) {
    const response = await apiClient.get(`/rbac/roles/${roleId}`);
    return response.data;
  },

  async createRole(roleData) {
    const response = await apiClient.post('/rbac/roles', roleData);
    return response.data;
  },

  async updateRole(roleId, updateData) {
    const response = await apiClient.put(`/rbac/roles/${roleId}`, updateData);
    return response.data;
  },

  async deleteRole(roleId) {
    const response = await apiClient.delete(`/rbac/roles/${roleId}`);
    return response.data;
  },

  // User Roles
  async getUserRoles(userId) {
    const response = await apiClient.get(`/rbac/users/${userId}/roles`);
    return response.data;
  },

  async assignRole(userId, roleId, tenantId, expiresAt = null) {
    const response = await apiClient.post(`/rbac/users/${userId}/roles`, {
      user_id: userId,
      role_id: roleId,
      tenant_id: tenantId,
      expires_at: expiresAt
    });
    return response.data;
  },

  async revokeRole(userId, roleId, tenantId = null) {
    const params = tenantId ? `?tenant_id=${tenantId}` : '';
    const response = await apiClient.delete(`/rbac/users/${userId}/roles/${roleId}${params}`);
    return response.data;
  },

  // Permissions
  async getPermissions(category = null) {
    const params = category ? `?category=${category}` : '';
    const response = await apiClient.get(`/rbac/permissions${params}`);
    return response.data;
  },

  async checkPermissions(permissions, tenantId = null) {
    const params = new URLSearchParams();
    params.append('permissions', permissions.join(','));
    if (tenantId) params.append('tenant_id', tenantId);
    const response = await apiClient.get(`/rbac/permissions/check?${params.toString()}`);
    return response.data;
  }
};

// Audit Service
export const auditService = {
  async getLogs(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/audit/logs?${params.toString()}`);
    return response.data;
  },

  async getLogDetail(logId) {
    const response = await apiClient.get(`/audit/logs/${logId}`);
    return response.data;
  },

  async getUserActivity(userId, days = 30) {
    const response = await apiClient.get(`/audit/logs/user/${userId}?days=${days}`);
    return response.data;
  },

  async getFinanceLogs(tenantId = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await apiClient.get(`/audit/logs/finance?${params.toString()}`);
    return response.data;
  },

  async getStats(days = 7, tenantId = null) {
    const params = new URLSearchParams();
    params.append('days', days);
    if (tenantId) params.append('tenant_id', tenantId);
    const response = await apiClient.get(`/audit/stats?${params.toString()}`);
    return response.data;
  },

  async exportLogs(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/audit/logs/export?${params.toString()}`);
    return response.data;
  }
};

// Admin Tools Service
export const adminToolsService = {
  // DB Lookup
  async getCollections() {
    const response = await apiClient.get('/admin-tools/db/collections');
    return response.data;
  },
  async queryCollection(name, params = {}) {
    const qs = new URLSearchParams(params).toString();
    const response = await apiClient.get(`/admin-tools/db/collections/${name}?${qs}`);
    return response.data;
  },
  // Logs
  async getServerLogs(logType = 'backend_err', lines = 200) {
    const response = await apiClient.get(`/admin-tools/logs/server?log_type=${logType}&lines=${lines}`);
    return response.data;
  },
  async getAuditLogs(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const response = await apiClient.get(`/admin-tools/logs/audit?${qs}`);
    return response.data;
  },
  async getApplicationLogs(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const response = await apiClient.get(`/admin-tools/logs/application?${qs}`);
    return response.data;
  },
  // Backup
  async getBackupStatus() {
    const response = await apiClient.get('/admin-tools/backup/status');
    return response.data;
  },
  async getBackupHistory() {
    const response = await apiClient.get('/admin-tools/backup/history');
    return response.data;
  },
  async createFullBackup() {
    const response = await apiClient.post('/admin-tools/backup/full', {}, { responseType: 'blob' });
    return response;
  },
  async createIncrementalBackup() {
    const response = await apiClient.post('/admin-tools/backup/incremental', {}, { responseType: 'blob' });
    return response;
  }
};
